import React, { useContext, useEffect, useState } from 'react';
import { Tab, Box, Tooltip } from '@mui/material';
import { TabStyles } from './Style'
import { dark } from 'styles/theme';
import { useTheme } from 'styles';
import { thirdly2 } from 'styles/theme';
import { Chip, Badge } from 'components/Form';
import { white } from 'styles/theme';
import { isNumber } from 'lodash';
import { AbilityContext } from 'components/Permissions/Can';

/**
 * @component ChallengesTabs
 * @description
 * קומפוננטת טאבים המציגה רשימת לשוניות עם תמיכה מתקדמת בסינון לפי הרשאות (Permissions).
 * הקומפוננטה מאפשרת להסתיר או להשבית טאבים ספציפיים על בסיס הרשאת המשתמש לפיצ'ר המסוים.
 * 
 * @param {Object} props
 * @param {Array} props.tabs - מערך אובייקטים המייצגים את הטאבים. כל אובייקט יכול להכיל:
 *   - `feature` (String): שם הפיצ'ר המשויך לטאב (לצורך בדיקת הרשאות).
 *   - `permissionHOC` ('hidden' | 'disabled'): מגדיר כיצד להתנהג כשאין הרשאה. 'hidden' (ברירת מחדל) מסתיר את הטאב, 'disabled' משבית אותו.
 *   - `label` (String): שם הטאב שיוצג למשתמש.
 *   - `tooltip` (String): טקסט שיופיע במעבר עכבר.
 *   - `soon` (Boolean): האם להציג תגית "בקרוב".
 *   - `badge` (Number/String): ערך שיוצג כתגית כמות (Badge) על הטאב.
 *   - `disabled` (Boolean): השבתת הטאב באופן ידני, ללא קשר להרשאה.
 * @param {Object} props.ability - אובייקט ניהול ההרשאות (כדוגמת CASL ability) לבדיקת הגישה.
 * @param {String} props.title - כותרת אופציונלית שתוצג לצד הטאבים.
 * @param {String} props.soon - הטקסט שיופיע בתוך רכיב ה"בקרוב".
 * @param {Boolean} props.hideBorder - האם להסתיר את הגבול (Border) התחתון.
 * @param {Function} props.handleTab - פונקציית קולבק המופעלת בעת החלפת טאב (מקבלת את האינדקס החדש).
 * @param {Number} props.defaultValue - אינדקס הטאב שיהיה פעיל כברירת מחדל.
 * @param {String} props.labelKey - שם השדה שממנו יש לשלוף את טקסט הטאב (כאשר אין `label`).
 * @param {String} props.variant - סגנון עיצוב הקומפוננטה.
 */
const ChallengesTabs = (props) => {
    const {
        tabs,
        title,
        soon,
        hideBorder,
        handleTab,
        defaultValue,
        labelKey,
        variant = 'default'
    } = props;

    const ability = useContext(AbilityContext)

    const [value, setValue] = useState(0);

    const { Layout, Fonts } = useTheme()

    const handleChange = (event, newValue) => {
        setValue(newValue);
        handleTab?.(newValue)
    };

    useEffect(() => {
        if (isNumber(defaultValue))
            setValue(defaultValue);
    }, [defaultValue])

    // סינון ועיבוד הטאבים בהתאם להרשאות הגישה
    const processedTabs = tabs.reduce((acc, tab) => {
        // במידה ויש לטאב הגדרת פיצ'ר ואובייקט ההרשאות קיים
        if (tab.feature && ability) {
            const hasPermission = ability.can("read", tab.feature);

            if (!hasPermission) {
                // אין הרשאה - נבדוק כיצד לטפל בטאב
                if (tab.permissionHOC === 'disabled') {
                    // מוסיפים את הטאב לרשימה אך מסמנים אותו כמושבת
                    acc.push({ ...tab, disabled: true });
                }
                // במידה והוא 'hidden' או שאין הגדרה (ברירת מחדל) - מדלגים עליו (לא מוסיפים ל-acc)
                return acc;
            }
        }

        // במידה ואין צורך בבדיקת הרשאה, או שיש הרשאה, נוסיף כרגיל
        acc.push(tab);
        return acc;
    }, []);

    return (
        <Box sx={{ ...Layout.fullWidth, color: dark, ...Layout.grow, ...Layout.scroll, ...Layout.col }}>
            <div style={{ ...Layout.rowHCenter }}>
                {title && <div style={{ width: '100px', ...Layout.shrink, ...Fonts.textSmall }}>{title}</div>}
                <Box sx={{
                    ...Layout.grow,
                    borderBottom: hideBorder ? 0 : 1,
                    borderColor: thirdly2,
                    ...Layout.rowHCenter,
                    ...Layout.justifyContentBetween,
                    ...Layout.shrink,
                    height: 45,
                    overflowY: "hidden"
                }}>
                    <TabStyles
                        value={value}
                        TabIndicatorProps={{
                            style: {
                                backgroundColor: dark,
                                fontWeight: 'bold'
                            }
                        }}
                        onChange={handleChange}
                        aria-label="basic tabs example">
                        {/* רינדור הטאבים לאחר הסינון */}
                        {processedTabs.map((tab, index) =>
                            <Tab
                                key={index}
                                disabled={tab.disabled}
                                label={
                                    <Tooltip placement='top' title={tab.tooltip || ''}>
                                        <div style={{ ...Layout.rowCenter }}>
                                            {tab.soon && <Chip borderColor="transparent">{soon}</Chip >}
                                            {tab.label || tab[labelKey]}
                                            {tab.badge && <Badge active={tab.active} bg={white} value={tab.badge} />}
                                        </div>
                                    </Tooltip>
                                }
                                id={`simple-tab-${index}`}
                                aria-controls={`simple-tabpanel-${index}`}
                            />
                        )}
                    </TabStyles>
                </Box>
            </div>
        </Box>
    );
}

export default ChallengesTabs;